import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { lazyWithRetry } from '../lib/lazyWithRetry';
import Shell from './layout/Shell';
import AdminGuard from './layout/AdminGuard';
import PortalGuard from './layout/PortalGuard';

// Lazy load pages
const Home = lazyWithRetry(() => import('../pages/Home'));
const Report = lazyWithRetry(() => import('../pages/Report'));
const MapView = lazyWithRetry(() => import('../pages/MapView'));
const AdminLogin = lazyWithRetry(() => import('../pages/admin/Login'));
const Login = lazyWithRetry(() => import('../pages/Login'));
const Dashboard = lazyWithRetry(() => import('../pages/admin/Dashboard'));
const Authorities = lazyWithRetry(() => import('../pages/admin/Authorities'));
const Users = lazyWithRetry(() => import('../pages/admin/Users'));
const CaseDetail = lazyWithRetry(() => import('../pages/admin/CaseDetail'));
const SetupPassword = lazyWithRetry(() => import('../pages/admin/SetupPassword'));
const Track = lazyWithRetry(() => import('../pages/Track'));
const Profile = lazyWithRetry(() => import('../pages/Profile'));
const TermsOfService = lazyWithRetry(() => import('../pages/legal/TermsOfService'));
const PrivacyPolicy = lazyWithRetry(() => import('../pages/legal/PrivacyPolicy'));
const AccessibilityStatement = lazyWithRetry(() => import('../pages/legal/AccessibilityStatement'));
const About = lazyWithRetry(() => import('../pages/About'));
const Contact = lazyWithRetry(() => import('../pages/Contact'));
const QuickAction = lazyWithRetry(() => import('../pages/QuickAction'));

const AdminDocs = lazyWithRetry(() => import('../pages/admin/AdminDocs'));
const Help = lazyWithRetry(() => import('../pages/Help'));
const Kommune = lazyWithRetry(() => import('../pages/Kommune'));

// Portal pages (authority users)
const PortalLogin = lazyWithRetry(() => import('../pages/portal/Login'));
const PortalDashboard = lazyWithRetry(() => import('../pages/portal/Dashboard'));
const PortalCaseDetail = lazyWithRetry(() => import('../pages/portal/CaseDetail'));
const PortalDocs = lazyWithRetry(() => import('../pages/portal/PortalDocs'));

const PageWrapper = ({ children }: { children: React.ReactNode }) => (
    <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
    >
        {children}
    </motion.div>
);

export default function AnimatedRoutes() {
    const location = useLocation();

    return (
        <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
                {/* Public Routes */}
                <Route path="/" element={<PageWrapper><Shell><Home /></Shell></PageWrapper>} />
                <Route path="/kommune" element={<PageWrapper><Shell><Kommune /></Shell></PageWrapper>} />
                <Route path="/for-kommuner" element={<Navigate to="/kommune" replace />} />
                <Route path="/feed" element={<PageWrapper><Shell><Home /></Shell></PageWrapper>} />
                <Route path="/map" element={<PageWrapper><Shell><MapView /></Shell></PageWrapper>} />
                <Route path="/report" element={<PageWrapper><Shell><Report /></Shell></PageWrapper>} />
                <Route path="/track" element={<PageWrapper><Shell><Track /></Shell></PageWrapper>} />
                <Route path="/track/:id" element={<PageWrapper><Shell><Track /></Shell></PageWrapper>} />
                <Route path="/profile" element={<PageWrapper><Shell><Profile /></Shell></PageWrapper>} />
                <Route path="/legal/terms" element={<PageWrapper><Shell><TermsOfService /></Shell></PageWrapper>} />
                <Route path="/legal/privacy" element={<PageWrapper><Shell><PrivacyPolicy /></Shell></PageWrapper>} />
                <Route path="/legal/accessibility" element={<PageWrapper><Shell><AccessibilityStatement /></Shell></PageWrapper>} />
                <Route path="/about" element={<PageWrapper><Shell><About /></Shell></PageWrapper>} />
                <Route path="/contact" element={<PageWrapper><Shell><Contact /></Shell></PageWrapper>} />
                <Route path="/help" element={<PageWrapper><Shell><Help /></Shell></PageWrapper>} />
                <Route path="/admin/setup-password" element={<PageWrapper><SetupPassword /></PageWrapper>} />
                <Route path="/action" element={<PageWrapper><QuickAction /></PageWrapper>} />
                <Route path="/docs" element={<Navigate to="/help" replace />} />
                <Route path="/docs/:docId" element={<Navigate to="/help" replace />} />

                <Route path="/login" element={<PageWrapper><Shell><Login /></Shell></PageWrapper>} />
                <Route path="/admin/login" element={<PageWrapper><AdminLogin /></PageWrapper>} />

                <Route path="/portal/login" element={<PageWrapper><PortalLogin /></PageWrapper>} />

                <Route element={<AdminGuard />}>
                    <Route path="/admin/dashboard" element={<PageWrapper><Dashboard /></PageWrapper>} />
                    <Route path="/admin/authorities" element={<PageWrapper><Authorities /></PageWrapper>} />
                    <Route path="/admin/users" element={<PageWrapper><Users /></PageWrapper>} />
                    <Route path="/admin/case/:id" element={<PageWrapper><CaseDetail /></PageWrapper>} />
                    <Route path="/admin/docs" element={<PageWrapper><AdminDocs /></PageWrapper>} />
                    <Route path="/admin/docs/:docId" element={<PageWrapper><AdminDocs /></PageWrapper>} />
                </Route>

                <Route element={<PortalGuard />}>
                    <Route path="/portal/dashboard" element={<PageWrapper><PortalDashboard /></PageWrapper>} />
                    <Route path="/portal/case/:id" element={<PageWrapper><PortalCaseDetail /></PageWrapper>} />
                    <Route path="/portal/docs" element={<PageWrapper><PortalDocs /></PageWrapper>} />
                    <Route path="/portal/docs/:docId" element={<PageWrapper><PortalDocs /></PageWrapper>} />
                </Route>
            </Routes>
        </AnimatePresence>
    );
}
